import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useGetAllClassesByGymListingIdQuery,
  useGetAllClassesCategoriesQuery,
  useGetGymListingPreviewByIdQuery,
} from "../../../api/queries";
import React, { useRef, useState } from "react";
import { IClassForTable } from "../../../models/Class";
import { ColumnsType } from "antd/es/table";
import { UNLIMITED_SLOTS_FOR_CLUBPASS_MEMBERS } from "../../../constants";
import MainTitle from "../../../components/ui/MainTitle/MainTitle";
import { Form, FormInstance, Table, TableProps } from "antd";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import searchIconWhite from "../../../assets/images/icons/search-icon-white.svg";
import editIcon from "../../../assets/images/icons/edit-dark.svg";
import ClassEditPopup from "./ClassEditPopup/ClassEditPopup";

const Classes = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);

  const [tableParams, setTableParams] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState<{ searchValue: string | null }>({ searchValue: null });
  const {
    data: classes = [],
    isLoading,
    refetch,
  } = useGetAllClassesByGymListingIdQuery(id, filters.searchValue);
  const { data: classesCategories = [] } = useGetAllClassesCategoriesQuery(null);
  const { data: gymListing } = useGetGymListingPreviewByIdQuery(id);

  const [selectedClassForEdit, setSelectedClassForEdit] = useState<IClassForTable | null>(null);

  const handleTableChange: TableProps<IClassForTable>["onChange"] = (pagination) => {
    setTableParams({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const onFinish = (values: { searchValue: string | undefined }) => {
    const model = {
      searchValue: values.searchValue ? values.searchValue : null,
    };
    setFilters(model);
  };

  const columns: ColumnsType<IClassForTable> = [
    {
      title: t("Class"),
      render: (data: IClassForTable) => {
        return data.name;
      },
    },
    {
      title: t("Category"),
      render: (data: IClassForTable) => {
        if (data.clubPassCategoryId) {
          const category = classesCategories.find((el) => el.id === data.clubPassCategoryId);
          return category ? category.name : "--";
        }
        return "-";
      },
    },
    {
      title: t("ClubPassSlots"),
      render: (data: IClassForTable) => {
        if (UNLIMITED_SLOTS_FOR_CLUBPASS_MEMBERS === data.numberSlotsClubPass) {
          return t("Unlimited");
        }
        return data.numberSlotsClubPass;
      },
    },
    {
      title: t("Price"),
      render: (data: IClassForTable) => {
        if (!data.priceForClubPass) return "-";
        return `€${data.priceForClubPass}`;
      },
    },
    {
      title: t("OpenGym"),
      render: (data: IClassForTable) => {
        if (!data.isOpenGymForClubPass) return "-";
        return t("Yes");
      },
    },
    {
      title: t("GymCalendar"),
      render: (data: IClassForTable) => {
        if (data.hasAnyEventsOnCalendar) {
          return <span style={{ color: "#32BC69" }}>{t("Available")}</span>;
        } else {
          return <span style={{ color: "#D93131" }}>{t("NotAvailable")}</span>;
        }
      },
    },
    {
      title: t("Visibility"),
      render: (data: IClassForTable) => {
        if (data.enableClubPass) {
          return <span style={{ color: "#32BC69" }}>{t("On")}</span>;
        } else {
          return <span style={{ color: "#D93131" }}>{t("Off")}</span>;
        }
      },
    },
    {
      title: t("Action"),
      render: (data: IClassForTable) => {
        return (
          <div>
            <button
              onClick={() => setSelectedClassForEdit(data)}
              style={{
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
                fontFamily: "Open Sans",
                fontSize: "16px",
                background: "transparent",
              }}
            >
              <img src={editIcon} alt={"edit"} />
              {t("Edit")}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className={"page-content gyms-page"}>
      <MainTitle
        text={
          gymListing?.licenseGymName
            ? `${t("AllServices")} — ${gymListing.licenseGymName}`
            : t("AllServices")
        }
      />

      <div className={"top-row"}>
        <Form
          className={"form"}
          ref={formRef}
          name="gym-listing-filter"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemInput
            name={"searchValue"}
            label={t("Search")}
            rules={{ required: false }}
            placeholder={t("SearchByClassName")}
            style={{
              width: "30%",
            }}
          />
          <div className={"btn-wrapper"}>
            <CustomButton
              text={""}
              type={"submit"}
              typeStyle={"fill"}
              icon={searchIconWhite}
              altIcon={"search"}
            />
          </div>
        </Form>
      </div>

      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={classes}
        pagination={{ ...tableParams, total: classes.length }}
        loading={isLoading}
        onChange={handleTableChange}
      />

      {selectedClassForEdit && id && (
        <ClassEditPopup
          closePopup={() => setSelectedClassForEdit(null)}
          item={selectedClassForEdit}
          getData={refetch}
          gymListingId={id}
          gymName={gymListing?.licenseGymName}
        />
      )}
    </div>
  );
};

export default Classes;
